import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
  Avatar,
  Chip,
  Alert,
  Paper,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Calendar, Users, Clock, ExternalLink } from "lucide-react";
import { axiosInstance } from "../../../utils/axios";

// GraphQL query to fetch team data (public - no auth required)
const GET_TEAM_PUBLIC = `
  query GetTeamPublic($id: ID!) {
    getTeamPublic(id: $id) {
      id
      name
      url
      about
      logo
      coverImage
      color
      members {
        id
        role
        status
        user {
          id
          name
          email
          avatar
        }
      }
      settings {
        visibility
      }
      createdAt
      updatedAt
    }
  }
`;

// Updated query to use the new dedicated resolver for team event types
const GET_TEAM_EVENT_TYPES = `
  query GetTeamEventTypes($teamId: ID!) {
    getTeamEventTypes(teamId: $teamId) {
      id
      title
      url
      description
      duration
      advanced {
        offerSeats {
          enabled
          seats
        }
      }
    }
  }
`;

function TeamPreview() {
  const [team, setTeam] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get ID from URL params
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const teamId = id || queryParams.get("teamId");

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setLoading(true);

        if (!teamId) {
          throw new Error("Team ID is required");
        }

        // Fetch team data (using public query - no auth required)
        const teamResponse = await axiosInstance.post("/graphql", {
          query: GET_TEAM_PUBLIC,
          variables: { id: teamId },
        });

        if (teamResponse.data?.errors) {
          throw new Error(teamResponse.data.errors[0].message);
        }

        const teamData =
          teamResponse.data?.getTeamPublic ||
          teamResponse.data?.data?.getTeamPublic;

        if (!teamData) {
          throw new Error("Team not found");
        }

        setTeam(teamData);

        // Fetch team's event types using the new dedicated resolver
        const eventTypesResponse = await axiosInstance.post("/graphql", {
          query: GET_TEAM_EVENT_TYPES,
          variables: {
            teamId: teamId,
          },
        });

        if (eventTypesResponse.errors) {
          throw new Error(eventTypesResponse.errors[0].message);
        }

        const eventTypesData =
          eventTypesResponse.getTeamEventTypes ||
          eventTypesResponse.data?.getTeamEventTypes ||
          [];

        setEventTypes(eventTypesData);
      } catch (error) {
        console.error("Error fetching team data:", error);
        setError(error.message || "An error occurred while fetching team data");
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [teamId]);

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";

    // Create a temporary div to parse HTML content
    const temp = document.createElement("div");
    temp.innerHTML = text;

    // Get text content without HTML tags
    const plainText = temp.textContent || temp.innerText || "";

    // Limit to specified character count
    if (plainText.length <= maxLength) {
      return plainText;
    }

    // Return truncated text with ellipsis
    return plainText.substring(0, maxLength) + "...";
  };

  // Get active members count
  const getActiveMembersCount = () => {
    if (!team || !team.members) return 0;
    return team.members.filter((m) => m.status === "active").length;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!team) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Team not found or invalid ID</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Team Profile Header */}
      <Box
        sx={{
          position: "relative",
          mb: 5,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Cover Image or Color Bar */}
        <Box
          sx={{
            height: 200,
            width: "100%",
            backgroundColor: team.color || "#3f51b5",
            backgroundImage: team.coverImage
              ? `url(${team.coverImage})`
              : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Team Info Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
            mt: -5,
            mx: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Team Logo/Avatar */}
          <Avatar
            src={team.logo}
            alt={team.name}
            sx={{
              width: 100,
              height: 100,
              border: "4px solid #fff",
              backgroundColor: team.color || "#3f51b5",
              fontSize: "2.5rem",
              boxShadow: 3,
            }}
          >
            {team.name && team.name.charAt(0).toUpperCase()}
          </Avatar>

          {/* Team Details */}
          <Box
            sx={{
              ml: { xs: 0, sm: 3 },
              mt: { xs: 2, sm: 5 },
              textAlign: { xs: "center", sm: "left" },
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "center", sm: "flex-start" },
                width: "100%",
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {team.name}
                </Typography>

                <Chip
                  icon={<Users size={16} />}
                  label={`${getActiveMembersCount()} members`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                />

                <Chip
                  icon={<Calendar size={16} />}
                  label="Booking Available"
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, ml: 1 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Team Description */}
      {team.about && (
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            About {team.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {team.about}
          </Typography>
        </Paper>
      )}

      {/* Header for Event Types Section */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Available Booking Types
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Event Types Grid */}
      <Grid container spacing={3}>
        {eventTypes.length > 0 ? (
          eventTypes.map((eventType) => (
            <Grid item xs={12} md={6} key={eventType.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  height: "100%",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {eventType.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {truncateText(eventType.description, 120)}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: "auto" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Clock
                        size={16}
                        style={{ marginRight: 8, color: "#666" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {eventType.duration} minutes
                      </Typography>
                    </Box>

                    {eventType.advanced?.offerSeats?.enabled && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Group event: Up to{" "}
                          {eventType.advanced.offerSeats.seats} participants
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <ExternalLink
                        size={16}
                        style={{ marginRight: 8, color: "#666" }}
                      />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {eventType.url}
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      component="a"
                      href={`/preview/preview-event-type?eventTypeId=${eventType.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mt: 2,
                        borderRadius: 2,
                        boxShadow: 2,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          color: "#fff",
                        },
                      }}
                    >
                      Book This Event
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                py: 2,
              }}
            >
              No event types available for this team.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}

export default TeamPreview;
